import { Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Pagination, Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import { AppointmentDetailReport, AppointmentsDetailReport } from 'model/BookingsReport';
import AppointmentsDetailTable from './AppointmentsDetailTable';
import {
    AppointmentDetailReportStatus,
    AppointmentsDetailReportState
} from 'reducers/appointmentsDetailReport/AppointmentsDetailReportState';
import { getAppointmentsDetailReport } from 'actions/insights/bookings/appointmentsDetail/AppointmentsDetailReportActions';

interface AppointmentsDetailProps {
    description?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: 'white',
            height: 'fit-content',

            [theme.breakpoints.up('md')]: {
                //padding: '50px 24px'
            }
        },
        mobileWrapper: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        }
    })
);

export const AppointmentsDetail: FunctionComponent<AppointmentsDetailProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const appointmentsLoading = useSelector<RootState, boolean>(state => state.appointmentsDetailReport.loading);

    const appointmentsStatus = useSelector<RootState, AppointmentDetailReportStatus>(
        state => state.appointmentsDetailReport.status
    );

    const appointmentsReport = useSelector<RootState, AppointmentsDetailReportState>(
        state => state.appointmentsDetailReport
    );

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone),
        to: moment().tz(timeZone)
    });

    const [appointmentsReportConverted, setAppointmentsReportConverted] =
        useState<Array<AppointmentDetailReport> | null>(null);

    const [page, setPage] = useState(1);

    const [pageSize, setPageSize] = useState(10);

    const [pagination, setPagination] = useState<Pagination>({
        page: Number(appointmentsReport.page),
        pageSize: Number(appointmentsReport.size),
        totalPages: Number(appointmentsReport.totalPages),
        totalRecords: Number(appointmentsReport.totalRecords)
    });

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    let url = `/appointments_detail_export_data?marketplace_id=${apiClientWrapper.marketplaceId}`;
    if (period?.from) url += `&start_date=${period?.from.format('YYYY/MM/DD')}`;
    if (period?.to) url += `&end_date=${period?.to.format('YYYY/MM/DD')}`;

    const exportFile = {
        name: `Appointments Detail ${marketplace.basics.businessName}.csv`,
        url: url,
        apiClientWrapper: apiClientWrapper
    };

    const calendar = {
        optionSelected: 'Today'
    };

    useEffect(() => {
        dispatch(
            getAppointmentsDetailReport(
                page,
                pageSize,
                period?.from.format('YYYY/MM/DD'),
                period?.to.format('YYYY/MM/DD')
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, page, pageSize]);

    useEffect(() => {
        if (appointmentsStatus === AppointmentDetailReportStatus.GettingSuccess) {
            setAppointmentsReportConverted(appointmentsReport.items);
            setPagination({
                page: Number(appointmentsReport.page),
                pageSize: Number(appointmentsReport.size),
                totalPages: Number(appointmentsReport.totalPages),
                totalRecords: Number(appointmentsReport.totalRecords)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentsStatus]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    const changePageHandler = (page: number) => {
        setPage(page);
    };

    const changePageSizeHandler = (pageSize: number) => {
        setPageSize(pageSize);
        setPage(1);
    };

    return (
        <div className={classes.container}>
            <div>
                <Typography color="textSecondary"> {props.description} </Typography>
            </div>
            <div className={classes.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>
            <AppointmentsDetailTable
                loading={appointmentsLoading}
                items={appointmentsReportConverted}
                pagination={pagination}
                changePageHandler={changePageHandler}
                changePageSizeHandler={changePageSizeHandler}
            />
        </div>
    );
};

export default AppointmentsDetail;
